import React, { useState, useEffect } from 'react';
import { useParams, useOutletContext } from 'react-router-dom';
import { contestAPI, contestTypeAPI, playerAPI, memberAPI, walletAPI, tournamentAPI } from '../services/api';
import './TournamentContests.css';

function TournamentContests() {
  const { tournamentId } = useParams();
  const { tournament, setTournament, setOnEditTournament } = useOutletContext();
  const [contests, setContests] = useState([]);
  const [contestTypes, setContestTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // State management
  const [activeTab, setActiveTab] = useState('overview'); // overview, create, manage
  const [selectedContestId, setSelectedContestId] = useState(null);
  const [showCancelled, setShowCancelled] = useState(false);
  
  // Contest creation form
  const [createForm, setCreateForm] = useState({
    contestTypeId: '',
    name: '',
    entryFee: '',
    subsidy: '',
    notes: ''
  });
  const [creating, setCreating] = useState(false);
  
  // Contest management
  const [selectedContest, setSelectedContest] = useState(null);
  const [managementMode, setManagementMode] = useState('participants'); // participants, payouts, settings
  
  // Payout management
  const [payoutStep, setPayoutStep] = useState('setup'); // setup, review, approve
  const [payouts, setPayouts] = useState([]);
  const [processingPayouts, setProcessingPayouts] = useState(false);
  
  // Tournament editing state
  const [showTournamentEdit, setShowTournamentEdit] = useState(false);
  const [editingTournament, setEditingTournament] = useState({});

  useEffect(() => {
    loadData();
  }, [tournamentId]);

  // Set up the edit tournament handler
  useEffect(() => {
    if (setOnEditTournament) {
      setOnEditTournament(() => handleEditTournament);
    }
    
    // Cleanup function to clear the handler when component unmounts
    return () => {
      if (setOnEditTournament) {
        setOnEditTournament(null);
      }
    };
  }, [setOnEditTournament]);

  useEffect(() => {
    if (selectedContestId) {
      loadContestDetails(selectedContestId);
    }
  }, [selectedContestId]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [contestsResponse, typesResponse] = await Promise.all([
        contestAPI.getTournamentContests(tournamentId),
        contestTypeAPI.getAll(true)
      ]);
      
      setContests(contestsResponse.data.data.contests || []);
      setContestTypes(typesResponse.data.data.contestTypes || []);
    } catch (err) {
      console.error('Error loading contest data:', err);
      setError('Failed to load contest data');
    } finally {
      setLoading(false);
    }
  };

  const loadContestDetails = async (contestId) => {
    try {
      const response = await contestAPI.getContest(contestId);
      setSelectedContest(response.data.data.contest);
    } catch (err) {
      console.error('Error loading contest details:', err);
      setError('Failed to load contest details');
    }
  };

  // Tournament editing handlers
  const handleEditTournament = () => {
    setEditingTournament({
      name: tournament?.name || '',
      status: tournament?.status || 'draft',
      feedbackDeadline: tournament?.feedbackDeadline || '',
      feedbackGracePeriodMinutes: tournament?.feedbackGracePeriodMinutes || 15
    });
    setShowTournamentEdit(true);
  };

  const handleSaveTournamentSettings = async () => {
    try {
      const updateData = {
        name: editingTournament.name?.trim() || tournament?.name,
        status: editingTournament.status,
        feedbackDeadline: editingTournament.feedbackDeadline || null,
        feedbackGracePeriodMinutes: parseInt(editingTournament.feedbackGracePeriodMinutes) || 15
      };

      const response = await tournamentAPI.update(tournamentId, updateData);
      if (response.data) {
        setTournament(response.data);
        setShowTournamentEdit(false);
      }
    } catch (err) {
      console.error('Error updating tournament:', err);
    }
  };

  const handleCancelTournamentEdit = () => {
    setShowTournamentEdit(false);
  };

  const handleCreateContest = async (e) => {
    e.preventDefault();
    
    if (!createForm.contestTypeId || !createForm.name) {
      alert('Please fill in required fields');
      return;
    }

    try {
      setCreating(true);
      
      const contestData = {
        contestTypeId: createForm.contestTypeId,
        name: createForm.name.trim(),
        entryFee: parseFloat(createForm.entryFee) || undefined,
        subsidy: parseFloat(createForm.subsidy) || undefined,
        notes: createForm.notes.trim() || undefined
      };

      await contestAPI.createContest(tournamentId, contestData);
      
      // Reset form and reload data
      setCreateForm({
        contestTypeId: '',
        name: '',
        entryFee: '',
        subsidy: '',
        notes: ''
      });
      
      await loadData();
      setActiveTab('overview');
      
    } catch (err) {
      console.error('Error creating contest:', err);
      alert(err.response?.data?.message || 'Failed to create contest');
    } finally {
      setCreating(false);
    }
  };

  const handleAddParticipant = async (playerId, playerName) => {
    if (!selectedContest) return;
    
    try {
      await contestAPI.addParticipant(selectedContest._id, playerId, playerName);
      await loadContestDetails(selectedContest._id);
    } catch (err) {
      console.error('Error adding participant:', err);
      alert(err.response?.data?.message || 'Failed to add participant');
    }
  };

  const handleProcessEntryFee = async (playerId) => {
    if (!selectedContest) return;
    
    try {
      await contestAPI.processEntryFee(selectedContest._id, playerId);
      await loadContestDetails(selectedContest._id);
    } catch (err) {
      console.error('Error processing entry fee:', err);
      alert(err.response?.data?.message || 'Failed to process entry fee');
    }
  };

  const handleDeleteContest = async (contestId) => {
    const contest = contests.find(c => c._id === contestId);
    if (!contest) return;
    
    const confirmMessage = `Are you sure you want to delete "${contest.name}"?\n\nThis action cannot be undone.`;
    if (!window.confirm(confirmMessage)) return;
    
    try {
      await contestAPI.cancelContest(contestId);
      await loadData();
      
      // If we're currently managing the deleted contest, go back to overview
      if (selectedContestId === contestId) {
        setSelectedContestId(null);
        setActiveTab('overview');
      }
      
    } catch (err) {
      console.error('Error deleting contest:', err);
      alert(err.response?.data?.message || 'Failed to delete contest');
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (!selectedContest) return;
    
    try {
      await contestAPI.updateContestStatus(selectedContest._id, newStatus);
      await loadContestDetails(selectedContest._id);
      await loadData(); // Refresh the overview list too
    } catch (err) {
      console.error('Error updating contest status:', err);
      alert(err.response?.data?.message || 'Failed to update contest status');
    }
  };

  const handleSetupPayouts = () => {
    if (!selectedContest) return;
    
    // Initialize payouts for paid participants
    const paidParticipants = selectedContest.participants.filter(p => p.status === 'paid');
    setPayouts([
      {
        playerId: '',
        playerName: '',
        amount: '',
        description: 'Winner'
      }
    ]);
    setPayoutStep('setup');
  };

  const handleAddPayout = () => {
    setPayouts([...payouts, {
      playerId: '',
      playerName: '',
      amount: '',
      description: ''
    }]);
  };

  const handleRemovePayout = (index) => {
    setPayouts(payouts.filter((_, i) => i !== index));
  };

  const handlePayoutChange = (index, field, value) => {
    const updatedPayouts = [...payouts];
    updatedPayouts[index][field] = value;
    setPayouts(updatedPayouts);
  };

  const handleReviewPayouts = () => {
    const totalPayout = payouts.reduce((sum, payout) => sum + (parseFloat(payout.amount) || 0), 0);
    if (totalPayout > (selectedContest.accounting?.totalPot || 0)) {
      alert(`Total payouts (${formatCurrency(totalPayout)}) exceed available pot (${formatCurrency(selectedContest.accounting?.totalPot || 0)})`);
      return;
    }
    setPayoutStep('review');
  };

  const handleProcessPayouts = async () => {
    if (!selectedContest) return;
    
    try {
      setProcessingPayouts(true);
      
      const validPayouts = payouts.filter(p => p.playerId && p.amount && parseFloat(p.amount) > 0);
      await contestAPI.recordPayout(selectedContest._id, validPayouts);
      
      await loadContestDetails(selectedContest._id);
      setPayoutStep('setup');
      setPayouts([]);
      
      alert('Payouts processed successfully!');
      
    } catch (err) {
      console.error('Error processing payouts:', err);
      alert(err.response?.data?.message || 'Failed to process payouts');
    } finally {
      setProcessingPayouts(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getStatusDisplay = (status) => {
    const statusConfig = {
      setup: { label: 'Setup', icon: '⚙️', className: 'status-setup' },
      collecting: { label: 'Collecting Entries', icon: '📝', className: 'status-collecting' },
      active: { label: 'Active', icon: '🏌️', className: 'status-active' },
      'paying-out': { label: 'Paying Out', icon: '💰', className: 'status-paying' },
      closed: { label: 'Closed', icon: '✅', className: 'status-closed' },
      cancelled: { label: 'Cancelled', icon: '❌', className: 'status-cancelled' }
    };
    
    return statusConfig[status] || { label: status, icon: '❓', className: 'status-unknown' };
  };

  if (loading) {
    return (
      <div className="tournament-contests loading">
        <div className="loading-spinner">Loading contests...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tournament-contests error">
        <div className="error-message">{error}</div>
      </div>
    );
  }

  return (
    <div className="tournament-contests">
      <div className="contests-header">
        <div className="contests-info">
          <h2>Contest Management</h2>
          <p className="contest-count">{contests.filter(c => c.status !== 'cancelled').length} active contests</p>
        </div>
        
        <div className="contests-actions">
          <button 
            className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('overview');
              setSelectedContestId(null);
            }}
          >
            Overview
          </button>
          <button 
            className={`tab-button ${activeTab === 'create' ? 'active' : ''}`}
            onClick={() => setActiveTab('create')}
          >
            Create Contest
          </button>
          {selectedContestId && (
            <button 
              className={`tab-button ${activeTab === 'manage' ? 'active' : ''}`}
              onClick={() => setActiveTab('manage')}
            >
              Manage: {selectedContest?.name || 'Contest'}
            </button>
          )}
        </div>
      </div>

      <div className="contests-content">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="tab-content overview-tab">
            <div className="contests-summary">
              {contests.length === 0 ? (
                <div className="no-contests">
                  <p>No contests created yet.</p>
                  <button 
                    className="create-first-contest-btn"
                    onClick={() => setActiveTab('create')}
                  >
                    Create First Contest
                  </button>
                </div>
              ) : (
                <>
                  {/* Active Contests */}
                  <div className="active-contests">
                    <h4>Active Contests ({contests.filter(c => c.status !== 'cancelled').length})</h4>
                    <div className="contests-list">
                      {contests.filter(contest => contest.status !== 'cancelled').map(contest => {
                        const statusInfo = getStatusDisplay(contest.status);
                        return (
                          <div key={contest._id} className="contest-summary-card">
                            <div className="contest-summary-header">
                              <div className="contest-name">{contest.name}</div>
                              <div className={`contest-status ${statusInfo.className}`}>
                                <span className="status-icon">{statusInfo.icon}</span>
                                <span className="status-text">{statusInfo.label}</span>
                              </div>
                            </div>
                            <div className="contest-summary-details">
                              <div className="contest-type">{contest.contestTypeId?.name || 'Unknown Type'}</div>
                              <div className="contest-stats">
                                <span>Entry: {formatCurrency(contest.entryFee)}</span>
                                <span>Pot: {formatCurrency(contest.accounting?.totalPot || 0)}</span>
                                <span>Players: {contest.accounting?.participantCount || 0}</span>
                              </div>
                            </div>
                            <div className="contest-actions">
                              <button 
                                className="manage-btn"
                                onClick={() => {
                                  setSelectedContestId(contest._id);
                                  setActiveTab('manage');
                                }}
                              >
                                Manage
                              </button>
                              <button 
                                className="delete-btn"
                                onClick={() => handleDeleteContest(contest._id)}
                                title="Delete this contest"
                              >
                                🗑️ Delete
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Cancelled Contests - Collapsible */}
                  {contests.filter(c => c.status === 'cancelled').length > 0 && (
                    <div className="cancelled-contests">
                      <button 
                        className="toggle-cancelled-btn"
                        onClick={() => setShowCancelled(!showCancelled)}
                      >
                        <span className={`toggle-icon ${showCancelled ? 'expanded' : ''}`}>▶</span>
                        Cancelled Contests ({contests.filter(c => c.status === 'cancelled').length})
                      </button>
                      {showCancelled && (
                        <div className="contests-list cancelled-list">
                          {contests.filter(contest => contest.status === 'cancelled').map(contest => {
                            const statusInfo = getStatusDisplay(contest.status);
                            return (
                              <div key={contest._id} className="contest-summary-card cancelled-card">
                                <div className="contest-summary-header">
                                  <div className="contest-name">{contest.name}</div>
                                  <div className={`contest-status ${statusInfo.className}`}>
                                    <span className="status-icon">{statusInfo.icon}</span>
                                    <span className="status-text">{statusInfo.label}</span>
                                  </div>
                                </div>
                                <div className="contest-summary-details">
                                  <div className="contest-type">{contest.contestTypeId?.name || 'Unknown Type'}</div>
                                  <div className="contest-stats">
                                    <span>Entry: {formatCurrency(contest.entryFee)}</span>
                                    <span>Pot: {formatCurrency(contest.accounting?.totalPot || 0)}</span>
                                    <span>Players: {contest.accounting?.participantCount || 0}</span>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {/* Create Tab */}
        {activeTab === 'create' && (
          <div className="tab-content create-tab">
            <form onSubmit={handleCreateContest} className="create-contest-form">
              <h3>Create New Contest</h3>
              
              <div className="form-group">
                <label htmlFor="contestType">Contest Type *</label>
                <select
                  id="contestType"
                  value={createForm.contestTypeId}
                  onChange={(e) => setCreateForm({...createForm, contestTypeId: e.target.value})}
                  required
                >
                  <option value="">Select a contest type...</option>
                  {contestTypes.map(type => (
                    <option key={type._id} value={type._id}>
                      {type.name} - {type.description}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="contestName">Contest Name *</label>
                <input
                  type="text"
                  id="contestName"
                  value={createForm.name}
                  onChange={(e) => setCreateForm({...createForm, name: e.target.value})}
                  placeholder="e.g., Saturday Morning Skins"
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="entryFee">Entry Fee ($)</label>
                  <input
                    type="number"
                    id="entryFee"
                    value={createForm.entryFee}
                    onChange={(e) => setCreateForm({...createForm, entryFee: e.target.value})}
                    placeholder="25.00"
                    min="0"
                    step="0.01"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="subsidy">Club Subsidy ($)</label>
                  <input
                    type="number"
                    id="subsidy"
                    value={createForm.subsidy}
                    onChange={(e) => setCreateForm({...createForm, subsidy: e.target.value})}
                    placeholder="100.00"
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="notes">Notes</label>
                <textarea
                  id="notes"
                  value={createForm.notes}
                  onChange={(e) => setCreateForm({...createForm, notes: e.target.value})}
                  placeholder="Additional information about the contest..."
                  rows={3}
                />
              </div>

              <div className="form-actions">
                <button type="submit" disabled={creating} className="create-btn">
                  {creating ? 'Creating...' : 'Create Contest'}
                </button>
                <button 
                  type="button" 
                  onClick={() => setActiveTab('overview')} 
                  className="cancel-btn"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Manage Tab */}
        {activeTab === 'manage' && selectedContest && (
          <div className="tab-content manage-tab">
            <div className="contest-header">
              <h3>{selectedContest.name}</h3>
              <div className={`contest-status ${getStatusDisplay(selectedContest.status).className}`}>
                {getStatusDisplay(selectedContest.status).icon} {getStatusDisplay(selectedContest.status).label}
              </div>
            </div>

            {/* Contest Status Management */}
            <div className="status-management">
              <h4>Contest Status</h4>
              <div className="status-buttons">
                {selectedContest.status === 'setup' && (
                  <button 
                    className="status-btn open-btn"
                    onClick={() => handleStatusChange('collecting')}
                  >
                    🟢 Open for Registration
                  </button>
                )}
                {selectedContest.status === 'collecting' && (
                  <>
                    <button 
                      className="status-btn active-btn"
                      onClick={() => handleStatusChange('active')}
                    >
                      🔴 Start Contest
                    </button>
                    <button 
                      className="status-btn setup-btn"
                      onClick={() => handleStatusChange('setup')}
                    >
                      📝 Back to Setup
                    </button>
                  </>
                )}
                {selectedContest.status === 'active' && (
                  <button 
                    className="status-btn payout-btn"
                    onClick={() => handleStatusChange('paying-out')}
                  >
                    💰 Begin Payouts
                  </button>
                )}
              </div>
            </div>

            <div className="contest-accounting">
              <div className="accounting-grid">
                <div className="accounting-item">
                  <span className="label">Total Pot:</span>
                  <span className="value">{formatCurrency(selectedContest.accounting?.totalPot || 0)}</span>
                </div>
                <div className="accounting-item">
                  <span className="label">Collected:</span>
                  <span className="value">{formatCurrency(selectedContest.accounting?.totalCollected || 0)}</span>
                </div>
                <div className="accounting-item">
                  <span className="label">Subsidy:</span>
                  <span className="value">{formatCurrency(selectedContest.accounting?.subsidy || 0)}</span>
                </div>
                <div className="accounting-item">
                  <span className="label">Paid Out:</span>
                  <span className="value">{formatCurrency(selectedContest.accounting?.totalPaidOut || 0)}</span>
                </div>
                <div className="accounting-item">
                  <span className="label">Remaining:</span>
                  <span className="value">{formatCurrency(selectedContest.accounting?.remaining || 0)}</span>
                </div>
                <div className="accounting-item">
                  <span className="label">Participants:</span>
                  <span className="value">{selectedContest.accounting?.participantCount || 0}</span>
                </div>
              </div>
            </div>

            <div className="management-tabs">
              <button 
                className={`tab-btn ${managementMode === 'participants' ? 'active' : ''}`}
                onClick={() => setManagementMode('participants')}
              >
                Participants ({selectedContest.participants.length})
              </button>
              <button 
                className={`tab-btn ${managementMode === 'payouts' ? 'active' : ''}`}
                onClick={() => setManagementMode('payouts')}
              >
                Payouts ({selectedContest.payouts.length})
              </button>
            </div>

            {/* Participants Management */}
            {managementMode === 'participants' && (
              <div className="participants-section">
                <div className="participants-header">
                  <h4>Contest Participants</h4>
                  <div className="participants-stats">
                    <span>Paid: {selectedContest.participants.filter(p => p.status === 'paid').length}</span>
                    <span>Pending: {selectedContest.participants.filter(p => p.status === 'pending').length}</span>
                  </div>
                </div>
                
                <div className="participants-list">
                  {selectedContest.participants.map((participant, index) => (
                    <div key={index} className="participant-item">
                      <div className="participant-info">
                        <div className="participant-name">{participant.playerName}</div>
                        <div className={`participant-status status-${participant.status}`}>
                          {participant.status === 'paid' ? '✅ Paid' : '⏳ Pending'}
                        </div>
                      </div>
                      <div className="participant-actions">
                        {participant.status === 'pending' && (
                          <button 
                            className="process-payment-btn"
                            onClick={() => handleProcessEntryFee(participant.playerId)}
                          >
                            Process Payment
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                  
                  {selectedContest.participants.length === 0 && (
                    <div className="no-participants">
                      <p>No participants yet. Players can join from the public tournament view.</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Payouts Management */}
            {managementMode === 'payouts' && (
              <div className="payouts-section">
                {payoutStep === 'setup' && (
                  <div className="payout-setup">
                    <div className="payout-header">
                      <h4>Setup Payouts</h4>
                      <button className="setup-payouts-btn" onClick={handleSetupPayouts}>
                        Start Payout Process
                      </button>
                    </div>
                    
                    {selectedContest.payouts.length > 0 && (
                      <div className="existing-payouts">
                        <h5>Previous Payouts</h5>
                        {selectedContest.payouts.map((payout, index) => (
                          <div key={index} className="payout-item">
                            <span>{payout.playerName}</span>
                            <span>{formatCurrency(payout.amount)}</span>
                            <span>{payout.description}</span>
                            <span className={payout.paidAt ? 'paid' : 'pending'}>
                              {payout.paidAt ? '✅ Paid' : '⏳ Pending'}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {payoutStep === 'setup' && payouts.length > 0 && (
                  <div className="payout-form">
                    <h4>Setup New Payouts</h4>
                    {payouts.map((payout, index) => (
                      <div key={index} className="payout-form-row">
                        <select
                          value={payout.playerId}
                          onChange={(e) => {
                            const participant = selectedContest.participants.find(p => p.playerId === e.target.value);
                            handlePayoutChange(index, 'playerId', e.target.value);
                            handlePayoutChange(index, 'playerName', participant?.playerName || '');
                          }}
                        >
                          <option value="">Select winner...</option>
                          {selectedContest.participants.filter(p => p.status === 'paid').map(participant => (
                            <option key={participant.playerId} value={participant.playerId}>
                              {participant.playerName}
                            </option>
                          ))}
                        </select>
                        <input
                          type="number"
                          placeholder="Amount"
                          value={payout.amount}
                          onChange={(e) => handlePayoutChange(index, 'amount', e.target.value)}
                          min="0"
                          step="0.01"
                        />
                        <input
                          type="text"
                          placeholder="Description"
                          value={payout.description}
                          onChange={(e) => handlePayoutChange(index, 'description', e.target.value)}
                        />
                        <button 
                          type="button" 
                          onClick={() => handleRemovePayout(index)}
                          className="remove-payout-btn"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                    
                    <div className="payout-actions">
                      <button onClick={handleAddPayout} className="add-payout-btn">
                        Add Another Payout
                      </button>
                      <button onClick={handleReviewPayouts} className="review-btn">
                        Review Payouts
                      </button>
                    </div>
                  </div>
                )}

                {payoutStep === 'review' && (
                  <div className="payout-review">
                    <h4>Review Payouts</h4>
                    <div className="review-summary">
                      <div className="review-item">
                        <span>Available Pot:</span>
                        <span>{formatCurrency(selectedContest.accounting?.remaining || 0)}</span>
                      </div>
                      <div className="review-item">
                        <span>Total Payouts:</span>
                        <span>{formatCurrency(payouts.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0))}</span>
                      </div>
                    </div>
                    
                    <div className="review-payouts">
                      {payouts.map((payout, index) => (
                        <div key={index} className="review-payout-item">
                          <span>{payout.playerName}</span>
                          <span>{formatCurrency(payout.amount)}</span>
                          <span>{payout.description}</span>
                        </div>
                      ))}
                    </div>
                    
                    <div className="review-actions">
                      <button onClick={() => setPayoutStep('setup')} className="back-btn">
                        Back to Setup
                      </button>
                      <button 
                        onClick={handleProcessPayouts} 
                        className="process-btn"
                        disabled={processingPayouts}
                      >
                        {processingPayouts ? 'Processing...' : 'Process Payouts'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Tournament Settings Modal */}
      {showTournamentEdit && (
        <div className="modal-overlay">
          <div className="tournament-settings-modal">
            <h3>Tournament Settings</h3>
            
            <div className="form-group">
              <label htmlFor="tournament-name">Tournament Name:</label>
              <input
                type="text"
                id="tournament-name"
                value={editingTournament.name || ''}
                onChange={(e) => setEditingTournament(prev => ({ ...prev, name: e.target.value }))}
              />
            </div>

            <div className="form-group">
              <label htmlFor="tournament-status">Status:</label>
              <select
                id="tournament-status"
                value={editingTournament.status || ''}
                onChange={(e) => setEditingTournament(prev => ({ ...prev, status: e.target.value }))}
              >
                <option value="draft">Draft</option>
                <option value="processing">Processing</option>
                <option value="published-for-feedback">Published for Feedback</option>
                <option value="completed">Completed</option>
                <option value="finalized">Finalized</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="feedback-deadline">Feedback Deadline:</label>
              <input
                type="datetime-local"
                id="feedback-deadline"
                value={editingTournament.feedbackDeadline || ''}
                onChange={(e) => setEditingTournament(prev => ({ ...prev, feedbackDeadline: e.target.value }))}
              />
              <small>Leave empty for no deadline</small>
            </div>

            <div className="form-group">
              <label htmlFor="grace-period">Grace Period (minutes):</label>
              <input
                type="number"
                id="grace-period"
                min="0"
                max="60"
                value={editingTournament.feedbackGracePeriodMinutes || 15}
                onChange={(e) => setEditingTournament(prev => ({ ...prev, feedbackGracePeriodMinutes: e.target.value }))}
              />
              <small>How long to allow feedback after the deadline</small>
            </div>

            <div className="modal-actions">
              <button onClick={handleCancelTournamentEdit} className="cancel-button">
                Cancel
              </button>
              <button onClick={handleSaveTournamentSettings} className="confirm-button">
                Save Settings
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TournamentContests;